// Rungs 6–7 of the ladder (plan §4), derived from stated principles and cited;
// none copies a figure. As with every rung, a move replaces ONE authored chord
// by one or more INSIDE its own span — which is what decides the shape these
// take. The Coltrane cycle is normally drawn across four bars of a lead sheet;
// written as one chord's span it becomes the two places the device actually
// fits without moving a barline: a long tonic that returns to itself, and the
// dominant's own bar, which is Countdown's tail.
//
// Rung 6 — Coltrane (Levine, The Jazz Theory Book, "Coltrane changes"): the
//   major-thirds cycle, three key centres a third apart, each reached by its
//   own dominant.
// Rung 7 — Modern (Felts ch. 13–16; Levine on hybrids and constant
//   structures): the modal cadence over a pedal (the chord heard as the I of
//   the mode the key puts it in, cadenced to by a chord carrying that mode's
//   characteristic note), hybrids (an upper structure over a foreign bass —
//   the triad tier and the `bass` field are what make these sound rather
//   than print), and constant structures (one quality planed by root motion
//   into the next chord).

import { CHORD_TYPES, type ChordTypeId } from '../chords';
import { qualityFamily } from '../corpus';
import { pitchClassOf } from '../notes';
import { admittedTensions } from '../tensions';
import {
  diatonicSevenths,
  hasSlashBass,
  heard,
  heardFromBass,
  mod12,
  proposal,
  regionAt,
  restate,
  spellRoot,
  symbolOf,
  type Proposal,
} from './helpers';
import type { ReharmMove } from './types';

/** A hybrid's upper structure (Felts ch. 14): a major or minor triad, or a
 *  ∆7, m7 or 7 seventh chord — never augmented, diminished or half-
 *  diminished. The seventh-chord layers are the textbook sus sounds (Dm7/G,
 *  F∆/G) and give the hybrid a fifth voice. */
const HYBRID_UPPER: ChordTypeId[] = ['maj', 'min', 'maj7', 'min7', 'dom7'];

const isMajorFamily = (type: ChordTypeId) => qualityFamily(type) === '∆';
const isDominantFamily = (type: ChordTypeId) => qualityFamily(type) === '7';

/** Signed shortest distance from `a` to `b` in semitones (−6…6). */
function shortestStep(a: number, b: number): number {
  const up = mod12(b - a);
  return up > 6 ? up - 12 : up;
}

// ---------------------------------------------------------------------------
// Rung 6 — Coltrane
// ---------------------------------------------------------------------------

/**
 * THE CYCLE ARRIVING ON A TONIC. Coltrane changes are normally drawn across
 * four bars, and a harmony layer cannot add a barline (plan §5), so the device
 * has to be written inside one chord's span — which turns out to be where it
 * belongs anyway: the bar that ARRIVES. The last three chords of the cycle
 * take the tonic's own bar and land on it,
 *
 *   … | C∆ |   →   … | E∆  G7  C∆ |
 *
 * so the bar still ends where the tune said it would and the chord before it
 * needs no change at all. The two key centres a major third apart are heard
 * on the way in rather than laid out over four bars.
 *
 * (The full six-change cycle is deliberately NOT offered: it needs eight beats
 * to read, an authored chord spans at most its own bar, and six changes
 * crammed into one is a blur rather than a reharmonization.)
 */
const coltraneApproach: ReharmMove = {
  id: 'coltrane-approach',
  rung: 6,
  name: 'Coltrane approach',
  hint: 'Two keys a third apart, arriving on the chord.',
  source: 'Levine, The Jazz Theory Book — Coltrane changes; Giant Steps (1959).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const beats = ctx.chordBeats[at];
    // Three chords need three beats to be played rather than flicked through.
    if (!isMajorFamily(chord.chordType) || beats < 3 || heardFromBass(ctx, at)) return [];
    const region = regionAt(ctx, at);
    const root = pitchClassOf(chord.root);
    const step = beats / 3;
    const spell = (interval: number) => spellRoot(mod12(root + interval), region);
    return [
      proposal(
        ctx,
        coltraneApproach,
        at,
        [
          { root: spell(4), chordType: 'maj7', beats: step },
          { root: spell(7), chordType: 'dom7', beats: step },
          restate(chord, step),
        ],
        `through ${spell(4)} and ${spell(7)} to ${chord.displayRoot}`,
      ),
    ];
  },
};

/**
 * COUNTDOWN: the dominant's own bar carries the cycle down to it. Coltrane's
 * reharmonization of a ii–V–I interpolates two key centres between the ii and
 * the V; written inside the V's span the figure is ♭VI∆ VII7 III∆ V7 of the
 * TARGET, ending on the original dominant — so the resolution the tune already
 * had is untouched, and the bar before it need not change at all.
 *
 *   … G7 | C∆   →   … A♭∆ B7 E∆ G7 | C∆
 */
const coltraneCountdown: ReharmMove = {
  id: 'coltrane-countdown',
  rung: 6,
  name: 'Countdown',
  hint: 'Two keys dropped in before the dominant resolves.',
  source: 'Levine, The Jazz Theory Book — Coltrane changes; Countdown (Giant Steps, 1959).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const next = ctx.sequence[at + 1];
    const beats = ctx.chordBeats[at];
    if (!isDominantFamily(chord.chordType) || beats < 4 || !next) return [];
    if (heardFromBass(ctx, at)) return [];
    // Only a dominant that really resolves: down a fifth, onto a major chord.
    const home = heard(ctx, at + 1);
    const target = home.rootPc;
    if (mod12(target - pitchClassOf(chord.root)) !== 5) return [];
    if (home.family !== '∆') return [];
    const region = regionAt(ctx, at);
    const step = beats / 4;
    const spell = (interval: number) => spellRoot(mod12(target + interval), region);
    return [
      proposal(
        ctx,
        coltraneCountdown,
        at,
        [
          { root: spell(8), chordType: 'maj7', beats: step },
          { root: spell(11), chordType: 'dom7', beats: step },
          { root: spell(4), chordType: 'maj7', beats: step },
          restate(chord, step),
        ],
        `two keys down to ${next.displayRoot}`,
      ),
    ];
  },
};

// ---------------------------------------------------------------------------
// Rung 7 — Modern
// ---------------------------------------------------------------------------

/** Felts ch. 13: a mode is heard through its CHARACTERISTIC note, and a
 *  modal cadence is a chord carrying that note resolving to the mode's I —
 *  best over a tonic pedal. Degrees are from the modal I (the chord's own
 *  root), so the table is the whole move: it is derived from the
 *  characteristic note, not listed per key. */
type ModeRow = {
  mode: string;
  /** The quality the mode's I chord has to be. */
  fits: (type: ChordTypeId) => boolean;
  /** The characteristic note, degrees above the modal I. */
  characteristic: number;
  cadences: { degree: number; type: ChordTypeId }[];
};
const isMinorFamily = (type: ChordTypeId) => qualityFamily(type) === 'm';
const MODES: ModeRow[] = [
  {
    mode: 'Dorian',
    fits: isMinorFamily,
    characteristic: 9,
    cadences: [
      { degree: 2, type: 'min7' },
      { degree: 5, type: 'maj' },
      { degree: 7, type: 'dom7' },
      { degree: 10, type: 'maj7' },
    ],
  },
  {
    mode: 'Phrygian',
    fits: isMinorFamily,
    characteristic: 1,
    cadences: [
      { degree: 1, type: 'maj7' },
      { degree: 8, type: 'min' },
    ],
  },
  {
    mode: 'Aeolian',
    fits: isMinorFamily,
    characteristic: 8,
    cadences: [
      { degree: 5, type: 'min' },
      { degree: 5, type: 'min7' },
      { degree: 8, type: 'maj7' },
      { degree: 10, type: 'dom7' },
      { degree: 2, type: 'min7b5' },
    ],
  },
  {
    mode: 'Lydian',
    fits: isMajorFamily,
    characteristic: 6,
    cadences: [
      { degree: 2, type: 'maj' },
      { degree: 2, type: 'dom7' },
      { degree: 11, type: 'min7' },
      { degree: 7, type: 'maj7' },
    ],
  },
  {
    // Mixolydian's I is I, I7 or I7sus4 — a ∆7 contradicts the ♭7. A written
    // maj7 is admitted anyway, because the cadence is written OVER the bass
    // and the ∆7 chord holds only the first half: the sus hybrid that follows
    // (Gm7/C, B♭/C over a C∆) never sounds the seventh — LaVerne's Mixolydian
    // re-hearing of a tonic in Tunes You Thought You Knew.
    mode: 'Mixolydian',
    fits: (type) => isDominantFamily(type) || type === 'maj' || type === 'maj7',
    characteristic: 10,
    cadences: [
      { degree: 10, type: 'maj' },
      { degree: 10, type: 'maj7' },
      { degree: 7, type: 'min7' },
    ],
  },
];

const modalCadence: ReharmMove = {
  id: 'modal-cadence',
  rung: 7,
  name: 'Modal cadence',
  hint: "The chord as the home of a mode, with that mode's cadence over its bass.",
  source: 'Felts ch. 13 (modal reharmonization); Levine — modal harmony over a pedal.',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const beats = ctx.chordBeats[at];
    const region = regionAt(ctx, at);
    // The cadence is written over the chord's root as its bass.
    if (!region || beats < 4 || hasSlashBass(chord)) return [];
    const root = pitchClassOf(chord.root);
    const scale = new Set(
      diatonicSevenths(region.minor).map((d) => mod12(region.tonicPc + d.degree)),
    );
    const isTonic = root === region.tonicPc;
    const half = beats / 2;
    const out: Proposal[] = [];
    for (const row of MODES) {
      if (!row.fits(chord.chordType)) continue;
      // The mode the KEY already puts this chord in (its characteristic note
      // is a scale tone — a ii in C is D Dorian's I), or, for the tonic
      // itself, any parallel mode of its quality; the melody gate decides.
      const diatonicReading = scale.has(mod12(root + row.characteristic));
      if (!diatonicReading && !isTonic) continue;
      for (const cadence of row.cadences) {
        const upper = spellRoot(mod12(root + cadence.degree), region);
        const spec = { root: upper, chordType: cadence.type, bass: chord.displayRoot, beats: half };
        out.push(
          proposal(
            ctx,
            modalCadence,
            at,
            [restate(chord, half), spec],
            `${row.mode}: ${symbolOf({ ...spec, beats: 0 })} over the ${chord.displayRoot} bass`,
          ),
        );
      }
    }
    return out;
  },
};

/**
 * HYBRIDS: a triad over a foreign bass — the chord's colour stated by three
 * notes and a bass note instead of a stack. DERIVED rather than listed, the
 * way the piano's upper structures are: every MAJOR or MINOR triad whose tones
 * are all chord tones or ADMITTED TENSIONS of the written chord, that adds at
 * least one real tension (a triad of chord tones is an inversion, not a
 * hybrid), and — the rule that makes a hybrid a hybrid rather than a voicing —
 * that contains NO THIRD above the bass. The missing third is the whole sound:
 * it is what leaves the chord open and unresolved between major and minor,
 * and it is why the derivation needs no list of "the hybrids". Diminished and
 * augmented triads are excluded for the same reason the piano's upper
 * structures exclude them: a hybrid is a plain triad over a bass.
 *
 *   C∆ → G/C  (5 7 9 over the root — the open lydian sound)
 *   C7 → B♭/C (♭7 9 11 — the textbook dominant hybrid)
 *
 * The triad tier is what lets these be WRITTEN, and the slash-bass work is
 * what lets them be played: the piano's left hand takes the bass and the
 * right hand the triad, which IS the texture.
 */
const hybrid: ReharmMove = {
  id: 'hybrid',
  rung: 7,
  name: 'Hybrid',
  hint: 'A chord over the bass instead of the full chord.',
  source: 'Felts ch. 14 (hybrid chord voicings); Levine, The Jazz Piano Book — slash chords.',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    // The upper layer sits over the chord's root; a chord with a written bass
    // is already a slash chord, and re-hybridising it would move that bass.
    if (hasSlashBass(chord)) return [];
    const region = regionAt(ctx, at);
    const root = pitchClassOf(chord.root);
    const tones = new Set(
      CHORD_TYPES[chord.chordType].intervals.map((iv) => mod12(root + iv)).concat(root),
    );
    const tensions = new Set(admittedTensions(chord.chordType).map((t) => mod12(root + t)));
    // Over a DOMINANT bass the upper layer may carry the 4: with no third
    // above the bass a hybrid on a dominant IS the sus sound (Dm7/G, F/G are
    // Felts's lead examples), which the chord's own tension table keeps out
    // only because a chart that wants it writes 7sus4.
    if (isDominantFamily(chord.chordType)) tensions.add(mod12(root + 5));
    const allowed = new Set([...tones, ...tensions]);
    const out: Proposal[] = [];
    // The thirds above the bass — a hybrid contains neither.
    const thirds = new Set([mod12(root + 3), mod12(root + 4)]);
    for (let upperRoot = 0; upperRoot < 12; upperRoot++) {
      for (const type of HYBRID_UPPER) {
        const pcs = [0, ...CHORD_TYPES[type].intervals].map((iv) => mod12(upperRoot + iv));
        if (!pcs.every((pc) => allowed.has(pc))) continue;
        // An upper layer made only of chord tones is an inversion of the
        // chord, not a hybrid: a hybrid says something the stack did not.
        if (!pcs.some((pc) => tensions.has(pc) && !tones.has(pc))) continue;
        // The bass is the chord's own root, so a chord ON it adds nothing —
        // and the bass note is never repeated in the upper layer (Felts):
        // G/D over a Dm7 restates the D and reads as a D chord again.
        if (mod12(upperRoot) === root || pcs.includes(root)) continue;
        // No third above the bass: the sound of a hybrid is the one it leaves
        // out.
        if (pcs.some((pc) => thirds.has(pc))) continue;
        const spec = {
          root: spellRoot(upperRoot, region),
          chordType: type,
          bass: chord.displayRoot,
          beats: ctx.chordBeats[at],
        };
        const kind = CHORD_TYPES[type].category === 'triad' ? 'a triad' : 'a seventh chord';
        out.push(
          proposal(
            ctx,
            hybrid,
            at,
            [spec],
            `${symbolOf({ ...spec, beats: 0 })}, ${kind} on the bass`,
          ),
        );
      }
    }
    return out;
  },
};

/**
 * CONSTANT STRUCTURES: one quality planed by root motion, the voicing held
 * constant while the roots walk into the next chord. Derived from the two
 * roots rather than listed — the plane is whatever equal step divides the
 * distance to where the music is already going, so it always ARRIVES.
 *
 *   C∆ … A∆  →  C∆ B∆ B♭∆ | A∆   (chromatic)
 *   C∆ … F♯∆ →  C∆ B♭∆ A♭∆ | F♯∆ (whole tone)
 */
const constantStructure: ReharmMove = {
  id: 'constant-structure',
  rung: 7,
  name: 'Constant structure',
  hint: 'The same chord shape walking into the next one.',
  source: 'Felts ch. 16 (constant structures); Levine — parallel/constant structures.',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const next = ctx.sequence[at + 1];
    const beats = ctx.chordBeats[at];
    // One shape planed whole: a slash chord would drag its bass along in
    // parallel, which is not the device.
    if (!next || beats < 2 || hasSlashBass(chord)) return [];
    const region = regionAt(ctx, at);
    const from = pitchClassOf(chord.root);
    const distance = shortestStep(from, heard(ctx, at + 1).rootPc);
    if (distance === 0) return [];
    const out: Proposal[] = [];
    for (const size of [1, 2, 3]) {
      if (Math.abs(distance) % size !== 0) continue;
      const count = Math.abs(distance) / size;
      // Two or three chords in the span: one is no plane, four is a scale.
      if (count < 2 || count > 3 || beats < count) continue;
      const direction = Math.sign(distance) * size;
      const step = beats / count;
      const chords = Array.from({ length: count }, (_, i) => ({
        root: i === 0 ? chord.displayRoot : spellRoot(mod12(from + direction * i), region),
        chordType: chord.chordType,
        beats: step,
      }));
      out.push(
        proposal(
          ctx,
          constantStructure,
          at,
          chords,
          `the same shape ${direction > 0 ? 'up' : 'down'} into ${next.displayRoot}`,
        ),
      );
    }
    return out;
  },
};

export const RUNG_6_TO_7_MOVES: readonly ReharmMove[] = [
  coltraneApproach,
  coltraneCountdown,
  modalCadence,
  hybrid,
  constantStructure,
];
